import { useCallback, useContext, useMemo, useState } from "react";
import { useQueryClient } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  ProjectVersionConflictError,
  useBmaOrgsList,
  useBmaSitesList,
  useRefreshSiteContributions,
  useUpsertProject,
} from "../api";
import { QK } from "../api/queryKeys";
import { UserContext } from "../auth";
import { PageHeader } from "../components/PageHeader";
import { SiteCombobox } from "../components/SiteCombobox";
import { useDocumentTitle } from "../utils/useDocumentTitle";

import styles from "./NewProjectRoute.module.css";

interface FormOrganization {
  name: string;
  type: string | null;
  // Set when the org row was prefilled from a picked BMA site's siteOwnerships.
  bma_org_id?: string | null;
}

// The project id IS the BMA site id, so picking a site is the whole form; the
// project's documents, Drive, email and Slack are set up from its Settings tab.
interface NewProjectForm {
  bma_site_id: string;
  name: string;
  address: string;
  target_system_size_kwp: number | null;
  organizations: FormOrganization[];
}

const findOrgName = (orgs: FormOrganization[], type: string): string | null =>
  orgs.find((o) => (o.type ?? "").toUpperCase() === type.toUpperCase())?.name || null;

export const NewProjectRoute = () => {
  useDocumentTitle("New project");
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [form, setForm] = useState<NewProjectForm | null>(null);
  // Shown when the picked site already has a project: a site has at most one.
  const [linkedProjectId, setLinkedProjectId] = useState<string | null>(null);

  // The list payload carries each site's organisations + target size. The
  // per-site /site/<id> read is denied to the cross-account RO token, so the
  // list is the only place the agents service can see a site's orgs.
  const bmaSitesQuery = useBmaSitesList();
  const bmaSites = useMemo(() => bmaSitesQuery.data ?? [], [bmaSitesQuery.data]);

  const onPickSite = useCallback(
    (siteId: string | null) => {
      const summary = siteId ? bmaSites.find((s) => s.id === siteId) : undefined;
      setLinkedProjectId(summary?.linked_project_id ?? null);
      if (!siteId || summary?.linked_project_id) {
        setForm(siteId ? { ...emptyFormFor(siteId) } : null);
        return;
      }
      setForm({
        bma_site_id: siteId,
        name: summary?.name ?? "",
        address: summary?.address ?? "",
        target_system_size_kwp: summary?.target_system_size_kwp ?? null,
        organizations: (summary?.organisations ?? []).map((o) => ({
          name: o.name || "",
          type: o.type || "",
          bma_org_id: o.bma_org_id,
        })),
      });
    },
    [bmaSites],
  );

  // BMA's siteOwnerships rarely include the EPC, so the operator can pick one
  // from BMA's canonical org list; it is stored as the project's primary_epc.
  const bmaOrgsQuery = useBmaOrgsList();
  const epcCandidates = useMemo(
    () =>
      (bmaOrgsQuery.data ?? []).filter((o) => (o.type ?? "").toUpperCase() === "EPC"),
    [bmaOrgsQuery.data],
  );
  const currentEpcName = form ? findOrgName(form.organizations, "EPC") ?? "" : "";
  const setPrimaryEpc = useCallback(
    (orgName: string) => {
      setForm((prev) => {
        if (!prev) return prev;
        const withoutEpc = prev.organizations.filter(
          (o) => (o.type ?? "").toUpperCase() !== "EPC",
        );
        if (!orgName) return { ...prev, organizations: withoutEpc };
        const candidate = epcCandidates.find((c) => c.name === orgName);
        return {
          ...prev,
          organizations: [
            ...withoutEpc,
            { name: orgName, type: "EPC", bma_org_id: candidate?.bma_org_id },
          ],
        };
      });
    },
    [epcCandidates],
  );
  // An empty EPC row gives the operator somewhere to pick one.
  const validationOrgs = useMemo(() => {
    const orgs = (form?.organizations ?? []).filter((o) => o.name.trim());
    if (orgs.some((o) => (o.type ?? "").toUpperCase() === "EPC")) return orgs;
    return [...orgs, { name: "", type: "EPC" }];
  }, [form]);

  const ready = form !== null && linkedProjectId === null;

  const upsertProject = useUpsertProject();
  const refreshSite = useRefreshSiteContributions();
  const queryClient = useQueryClient();
  const [createError, setCreateError] = useState<string | null>(null);
  const create = useCallback(async () => {
    if (!form || !ready || upsertProject.isLoading) return;
    setCreateError(null);
    const id = form.bma_site_id;
    try {
      await upsertProject.mutateAsync({
        project_id: id,
        // Create-only: the site list is cached, so a project made since it loaded
        // must come back as a conflict rather than be overwritten.
        expected_version: 0,
        bma_site_id: id,
        name: form.name.trim() || null,
        address: form.address.trim() || null,
        target_system_size_kwp: form.target_system_size_kwp,
        primary_epc: findOrgName(form.organizations, "EPC"),
        status: "submitted",
        updated_by: user?.email || "operator",
      });
    } catch (e) {
      if (e instanceof ProjectVersionConflictError) {
        setLinkedProjectId(id);
      } else {
        setCreateError((e as Error).message || "unknown error");
      }
      void queryClient.invalidateQueries([QK.bmaSitesList]);
      return;
    }
    void queryClient.invalidateQueries([QK.bmaSitesList]);
    // Pulls the site's data into the project's contributions; it outlives this
    // route and invalidates the project on success.
    refreshSite.mutate(id);
    navigate(`/projects/${encodeURIComponent(id)}/settings`);
  }, [form, ready, upsertProject, refreshSite, queryClient, navigate, user]);

  return (
    <>
      <PageHeader
        title="New project"
        description="Pick the Production site this project is for. The project name and details are taken from the site."
      />
      <div className={styles.page}>
        <section className={styles.section}>
          <h2 className={styles.h2}>Production site</h2>

          <div className={styles.grid}>
            <Field
              label="Linked Production site"
              hint="Select the production site. The project is created against it."
              wide
            >
              {bmaSitesQuery.isLoading ? (
                <div className={styles.muted}>Loading Production sites...</div>
              ) : bmaSitesQuery.error != null ? (
                <div className={styles.warn}>
                  Couldn&apos;t load Production sites: {(bmaSitesQuery.error as Error).message}
                </div>
              ) : bmaSites.length === 0 ? (
                <div className={styles.muted}>
                  No Production sites are available for direct linking from this
                  environment.
                </div>
              ) : (
                <SiteCombobox
                  sites={bmaSites}
                  value={form?.bma_site_id ?? null}
                  onSelect={onPickSite}
                  currentProjectId=""
                />
              )}
              {linkedProjectId && (
                <div className={styles.warn}>
                  This Production site already has a project, and a site can only have
                  one. Open it instead:{" "}
                  <Link
                    to={`/projects/${encodeURIComponent(linkedProjectId)}`}
                    className={styles.warnLink}
                  >
                    {linkedProjectId}
                  </Link>
                  .
                </div>
              )}
            </Field>
          </div>

          {ready && (
            <>
              <div className={styles.validateNote}>
                Confirm these details match the site you intended before creating the
                project.
              </div>
              <div className={styles.grid}>
                <Field label="Project name" wide>
                  <div
                    className={`${styles.readonlyValue} ${form.name ? "" : styles.empty}`}
                  >
                    {form.name || "(uses the site name)"}
                  </div>
                </Field>
                {form.address && (
                  <Field label="Site address" wide>
                    <div className={styles.readonlyValue}>{form.address}</div>
                  </Field>
                )}
                <Field label="Organisations on this site" wide>
                  <OrgsValidationList
                    orgs={validationOrgs}
                    currentEpcName={currentEpcName}
                    epcCandidates={epcCandidates}
                    epcLoading={bmaOrgsQuery.isLoading}
                    onPickEpc={setPrimaryEpc}
                  />
                </Field>
              </div>
            </>
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.submitBar}>
            <button
              type="button"
              className={styles.submitBtn}
              disabled={!ready || upsertProject.isLoading}
              onClick={create}
            >
              {upsertProject.isLoading ? "Creating…" : "Create project"}
            </button>
            <span className={styles.submitHint}>
              {ready
                ? "Next, connect Google Drive, email and Slack from the project's Settings."
                : linkedProjectId
                  ? "This site already has a project - open it above."
                  : "Select a site to start."}
            </span>
          </div>
          {createError && (
            <div className={styles.warn}>Couldn&apos;t create the project: {createError}</div>
          )}
        </section>
      </div>
    </>
  );
};

const emptyFormFor = (siteId: string): NewProjectForm => ({
  bma_site_id: siteId,
  name: "",
  address: "",
  target_system_size_kwp: null,
  organizations: [],
});

const Field = ({
  label,
  hint,
  wide,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: React.ReactNode;
}) => (
  <div className={wide ? styles.fieldWide : styles.field}>
    <label className={styles.fieldLabel}>{label}</label>
    {hint ? <div className={styles.fieldHint}>{hint}</div> : null}
    {children}
  </div>
);

// The "Organisations on this site" list. Renders BMA's siteOwnership
// orgs plus a Primary EPC row. The EPC row is special: it carries an
// inline "Edit" affordance that swaps the row's name + tag for a
// dropdown of canonical EPC orgs from BMA. On pick the row reverts to
// display mode. Operator never leaves the orgs list - matches the
// "one canonical list" UX Noaa called out (PR #112).
const OrgsValidationList = ({
  orgs,
  currentEpcName,
  epcCandidates,
  epcLoading,
  onPickEpc,
}: {
  orgs: { name: string; type: string | null }[];
  currentEpcName: string;
  epcCandidates: { bma_org_id: string; name: string; type: string }[];
  epcLoading: boolean;
  onPickEpc: (orgName: string) => void;
}) => {
  const [editingEpc, setEditingEpc] = useState(false);
  // Auto-open the editor for an empty EPC row so the operator sees
  // the picker without having to discover an "Edit" link first.
  // Stays open until they commit a pick.
  const epcIsEmpty = !currentEpcName.trim();
  if (orgs.length === 0) {
    return (
      <div className={styles.muted}>
        No organisations are recorded against this site in Production.
      </div>
    );
  }
  return (
    <div className={styles.orgReadList}>
      {orgs.map((o, i) => {
        const isEpcRow = (o.type ?? "").toUpperCase() === "EPC";
        if (!isEpcRow) {
          return (
            <div key={i} className={styles.orgReadItem}>
              <span>{o.name}</span>
              {o.type && (
                <span className={styles.orgReadType}>{o.type}</span>
              )}
            </div>
          );
        }
        const editing = editingEpc || epcIsEmpty;
        if (editing) {
          return (
            <div key={i} className={styles.orgReadItem}>
              <select
                className={styles.orgInlineSelect}
                value={currentEpcName}
                onChange={(e) => {
                  onPickEpc(e.target.value);
                  setEditingEpc(false);
                }}
                disabled={epcLoading}
              >
                <option value="">
                  {epcLoading ? "Loading EPCs…" : "(none)"}
                </option>
                {currentEpcName &&
                  !epcCandidates.some((c) => c.name === currentEpcName) && (
                    <option value={currentEpcName}>
                      {currentEpcName} (not in Production org list)
                    </option>
                  )}
                {epcCandidates.map((c) => (
                  <option key={c.bma_org_id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              <span className={styles.orgReadType}>EPC</span>
              {!epcIsEmpty && (
                <button
                  type="button"
                  className={styles.orgInlineCancel}
                  onClick={() => setEditingEpc(false)}
                >
                  Cancel
                </button>
              )}
            </div>
          );
        }
        return (
          <div key={i} className={styles.orgReadItem}>
            <span>{o.name}</span>
            <span className={styles.orgReadType}>EPC</span>
            <button
              type="button"
              className={styles.orgInlineEdit}
              onClick={() => setEditingEpc(true)}
            >
              Edit
            </button>
          </div>
        );
      })}
    </div>
  );
};
